import React, { useState } from 'react';
import styled from 'styled-components'
import useAsyncEffect from 'use-async-effect';
import { FaSpinner } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../api/api';
import { CustomerContactItem } from '../model/CustomerContactItem';
import SortableTable from './table/SortableTable';

const Style = styled.div`
.controls {
    margin: 1rem 0;
    display: flex;
    align-items: center;
    gap: 1rem;
}
table {
    margin-top: 1rem;
    width: 100%;
}
.guessed-note {
    font-size: 0.85rem;
    color: #6b7280;
    margin-bottom: 0.5rem;
}
`;

type EmailMarketingFilter = 'all' | 'without' | 'with';

// row model must be the last element (SortableTable passes it to cellFormatter)
const toRow = (x:CustomerContactItem) => {
    const emails = [...x.emails, ...x.guessedEmails].join(', ');
    const name = normalizeCompanyName(x.companyName);
    const company = x.guessedEmails.length ? `${name} *` : name;
    return [`${x.ticker}:${x.exchange}`, company, emails, x.emailMarketingProviderType, x];
}

const cellFormatter = (columnName:string, cell:any, rowModel:CustomerContactItem) => {
    if(columnName === 'Ticker') return <Link to={`/stocks/${rowModel.ticker}:${rowModel.exchange}`}>{cell}</Link>;
    return null;
}

// "european-metals-holdings-limited" / "MINBOS RESOURCES LIMITED" -> "European Metals Holdings Limited";
// words with digits (AML3D) and short acronyms (ALB) and mixed-case words (AuMega) are left alone
const normalizeWord = (w:string) => {
    if (/\d/.test(w)) return w;
    if (w === w.toUpperCase() && w.length <= 3) return w;
    if (w === w.toUpperCase() || w === w.toLowerCase()) return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    return w;
};
const normalizeCompanyName = (name:string) =>
    (name || '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().split(' ').map(normalizeWord).join(' ');

// RFC 4180 quoting, same as ContactComponent's exporter
const csvField = (value: string) => {
    const s = value ?? '';
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const BillableStocksPage:React.FC = () => {

    const [ items, setItems ] = useState<CustomerContactItem[]>();
    const [ filter, setFilter ] = useState<EmailMarketingFilter>('all');

    useAsyncEffect(async (isMounted)=>{
        const result = await api().getBillableStockContacts();
        if(isMounted()){
            setItems(result);
        }
    }, []);

    // demo-only setups (no_reply reply-to, <=5 subscribers) count as without email marketing
    const withoutEmailMarketing = (x:CustomerContactItem) => x.emailMarketingProviderType === 'None' ||
        (x.mailChimpReplyTo === 'no_reply@investi-mailer.com.au' && (x.subscriberCount ?? 0) <= 5);
    const filtered = (items || []).filter(x =>
        filter === 'all' ? true :
        filter === 'without' ? withoutEmailMarketing(x) : !withoutEmailMarketing(x));

    const uniqueEmails = new Set(filtered.flatMap(x => x.emails.map(e => e.toLowerCase())));
    const guessed = filtered.filter(x => x.guessedEmails.length > 0);

    // same shape the Email Marketing contacts importer accepts (Email,LastName,FirstName,Status,Tags)
    const downloadCsv = () => {
        const seen = new Set<string>();
        const rows:string[] = [];
        for(const item of filtered){
            for(const email of item.emails){
                if(seen.has(email.toLowerCase())) continue;
                seen.add(email.toLowerCase());
                rows.push([email, normalizeCompanyName(item.companyName), '', 'subscribed', ''].map(csvField).join(','));
            }
        }
        const csvContent = ['Email,LastName,FirstName,Status,Tags', ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `billable-stocks-${filter}-email-marketing.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    return <Style>
        <h2>Billable Stocks</h2>
        { !items && <FaSpinner className="fa-spin" size={24} color="grey" /> }
        { !!items && <>
            <div className='controls'>
                <label>Email marketing:{' '}
                    <select value={filter} onChange={e => setFilter(e.target.value as EmailMarketingFilter)}>
                        <option value='all'>All</option>
                        <option value='without'>Without email marketing</option>
                        <option value='with'>With email marketing</option>
                    </select>
                </label>
                <span>{filtered.length} stocks, {uniqueEmails.size} unique emails</span>
                <button onClick={downloadCsv}>Download Contacts CSV</button>
            </div>
            { guessed.length > 0 &&
                <div className='guessed-note'>* best-guess info@ address (Xero only holds an accounts@ address) — not included in the CSV</div> }
            <SortableTable
                headers={['Ticker', 'Company', 'Email(s)', 'Email Marketing']}
                data={filtered.map(toRow)}
                sortIndex={0}
                sortDirection='asc'
                cellFormatter={cellFormatter}
                />
        </> }
    </Style>
}

export default BillableStocksPage;
